import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Route, Switch, useLocation, useRouteMatch } from "react-router";
import { queryCache, useQuery } from "react-query";
import { Helmet } from "react-helmet";
import Container from "@material-ui/core/Container";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { endOfTitle } from "../../../constants/constants";
import { AdapterLink } from "../../../components/Adopter";
import { Routes } from "../../../constants/routes";
import Error from "../../../components/Error";
import theme from "../../../themes";
import { StudentApplications } from "../../application/components/StudentApplications";
import MessagesPageWrapper from "../../message/MessagesPageWrapper";
import BillingPage from "../../billing/pages/BillingPage";
import { useAuth } from "../../auth/hooks/useAuth";
import { useWizard } from "../../wizard/context";
import { StudentProfileWidget } from "../components/managedStudent/StudentProfileWidget";
import { UserRole } from "../../user/enums/UserRole";
import { useUpdateProfileConfig } from "../../user/userAccount/modals/EditProfile/config";
import useUserQueries from "../../user/hooks/useUserQueries";
import { useUserAccount } from "../../user/userAccount/context";

const useStyles = makeStyles(() => ({
  tabs: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  tabLink: {
    "&:hover": {
      textDecoration: "none",
    },
    "@media (min-width: 520px)": {
      minWidth: "unset",
    },
  },
}));

export const ManagedStudentAccountPage = () => {
  const classes = useStyles();

  const { user } = useAuth();
  const { user: accountUser } = useUserAccount();
  const { url: path } = useRouteMatch();
  const { pathname } = useLocation();
  const { studentId }: { studentId: string } = useParams();
  const { fetchOne } = useUserQueries();

  const pieces = pathname.split(`${path}`);
  const value = pieces[pieces.length - 1].replace(/^\/|\/$/g, "");
  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);
  const currentRoute = isAdminOrSupport
    ? "/0/user/:userId/edit/student/:studentId"
    : "/0/student/:studentId";

  const { openWizard } = useWizard();

  const { data } = useQuery<any>("user_account_" + studentId, () => {
    return fetchOne(studentId);
  });

  const student = data?.data;
  const isAgent =
    (isAdminOrSupport ? accountUser?.role : user.role) === UserRole.Agent;
  const userId = isAdminOrSupport ? accountUser?.id : user.id;

  const agentId = isAgent ? userId : undefined;
  const parentId = !isAgent ? userId : undefined;
  const getEditStudentConfig = useUpdateProfileConfig(student);

  const tabs = useMemo(
    () => [
      {
        label: "Applications",
        component: () => <StudentApplications studentId={studentId} />,
        path: `/${Routes.APPLICATIONS}`,
      },
      {
        label: "Billing",
        component: () => (
          <BillingPage
            agentId={agentId}
            parentId={parentId}
            studentId={studentId}
            showFilter={false}
          />
        ),
        path: `/${Routes.BILLING}`,
      },
      {
        label: "Messages",
        component: () => <MessagesPageWrapper studentId={studentId} />,
        path: `/${Routes.MESSAGES}`,
      },
    ],
    [],
  );

  const editStudent = async () => {
    if (await openWizard(getEditStudentConfig)) {
      await queryCache.invalidateQueries();
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Student{endOfTitle}</title>
      </Helmet>
      <StudentProfileWidget
        id={studentId}
        actionButtonType={"edit"}
        onActionButtonClick={editStudent}
      />
      <Tabs value={value} variant="scrollable" className={classes.tabs}>
        {tabs.map((item, index) => (
          <Tab
            component={AdapterLink}
            className={classes.tabLink}
            key={index}
            label={item.label}
            value={item.path.replace(/^\/|\/$/g, "")}
            to={path + item.path}
          />
        ))}
      </Tabs>
      <Switch>
        {tabs.map((item, index) => (
          <Route
            key={index}
            exact
            path={currentRoute + item.path}
            component={item.component}
          />
        ))}
        <Route
          path={`${path}/*`}
          component={() => <Error text={"Not found"} />}
        />
      </Switch>
    </Container>
  );
};
